import { useEffect } from 'react';

// Schema types for structured data
interface OrganizationSchema {
  type: 'Organization';
  name: string;
  url: string;
  logo: string;
  description: string;
}

interface WebApplicationSchema {
  type: 'WebApplication';
  name: string;
  description: string;
  applicationCategory: string;
  operatingSystem: string;
  offers?: {
    price: string;
    priceCurrency: string;
  };
}

interface FAQSchema {
  type: 'FAQPage';
  questions: Array<{
    question: string;
    answer: string;
  }>;
}

interface BreadcrumbSchema {
  type: 'BreadcrumbList';
  items: Array<{
    name: string;
    url: string;
  }>;
}

interface CourseSchema {
  type: 'Course';
  name: string;
  description: string;
  provider: string;
  providerUrl: string;
  url: string;
  inLanguage?: string;
  numberOfQuestions?: number;
  duration?: string;
  educationalLevel?: string;
  subject?: string;
}

interface ProductSchema {
  type: 'Product';
  name: string;
  description: string;
  url: string;
  brand: string;
  price: number;
  priceCurrency?: string;
  availability?: string;
  ratingValue?: number;
  reviewCount?: number;
}

interface ItemListSchema {
  type: 'ItemList';
  name: string;
  description: string;
  url: string;
  items: Array<{
    name: string;
    url: string;
    description?: string;
    price?: number;
  }>;
}

type SchemaData =
  | OrganizationSchema
  | WebApplicationSchema
  | FAQSchema
  | BreadcrumbSchema
  | CourseSchema
  | ProductSchema
  | ItemListSchema;

/**
 * StructuredData Component
 *
 * Injects JSON-LD structured data into the document head.
 * This helps Google understand your content and can enable rich results.
 *
 * @example
 * <StructuredData
 *   data={{
 *     type: 'WebApplication',
 *     name: 'Hướng nghiệp CAT',
 *     description: 'Công cụ trắc nghiệm hướng nghiệp...',
 *     applicationCategory: 'EducationalApplication',
 *     operatingSystem: 'All',
 *   }}
 * />
 */
export function StructuredData({ data, id }: { data: SchemaData; id?: string }) {
  useEffect(() => {
    const scriptId = `structured-data-${data.type}${id ? `-${id}` : ''}`;
    let script = document.getElementById(scriptId);

    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.setAttribute('type', 'application/ld+json');
      document.head.appendChild(script);
    }

    const jsonLd = generateJsonLd(data);
    script.textContent = JSON.stringify(jsonLd);

    return () => {
      script?.remove();
    };
  }, [data]);

  return null;
}

function generateJsonLd(data: SchemaData): Record<string, unknown> {
  switch (data.type) {
    case 'Organization':
      return {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: data.name,
        url: data.url,
        logo: data.logo,
        description: data.description,
        address: {
          '@type': 'PostalAddress',
          addressCountry: 'VN',
        },
      };

    case 'WebApplication':
      return {
        '@context': 'https://schema.org',
        '@type': 'WebApplication',
        name: data.name,
        description: data.description,
        applicationCategory: data.applicationCategory,
        operatingSystem: data.operatingSystem,
        inLanguage: 'vi',
        ...(data.offers && {
          offers: {
            '@type': 'Offer',
            price: data.offers.price,
            priceCurrency: data.offers.priceCurrency,
          },
        }),
      };

    case 'FAQPage':
      return {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: data.questions.map(q => ({
          '@type': 'Question',
          name: q.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: q.answer,
          },
        })),
      };

    case 'BreadcrumbList':
      return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: data.items.map((item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: item.name,
          item: item.url,
        })),
      };

    case 'Course':
      return {
        '@context': 'https://schema.org',
        '@type': 'Course',
        name: data.name,
        description: data.description,
        url: data.url,
        inLanguage: data.inLanguage || 'vi',
        provider: {
          '@type': 'Organization',
          name: data.provider,
          url: data.providerUrl,
        },
        ...(data.educationalLevel && { educationalLevel: data.educationalLevel }),
        ...(data.subject && {
          about: {
            '@type': 'Thing',
            name: data.subject,
          },
        }),
        ...(data.duration && {
          hasCourseInstance: {
            '@type': 'CourseInstance',
            courseMode: 'online',
            duration: data.duration,
          },
        }),
      };

    case 'Product':
      return {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: data.name,
        description: data.description,
        url: data.url,
        brand: {
          '@type': 'Brand',
          name: data.brand,
        },
        offers: {
          '@type': 'Offer',
          price: data.price,
          priceCurrency: data.priceCurrency || 'VND',
          availability: data.availability || 'https://schema.org/InStock',
        },
        ...(data.ratingValue &&
          data.reviewCount && {
            aggregateRating: {
              '@type': 'AggregateRating',
              ratingValue: data.ratingValue,
              reviewCount: data.reviewCount,
            },
          }),
      };

    case 'ItemList':
      return {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: data.name,
        description: data.description,
        url: data.url,
        numberOfItems: data.items.length,
        itemListElement: data.items.map((item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: item.name,
          url: item.url,
          ...(item.description && {
            item: {
              '@type': 'Course',
              name: item.name,
              url: item.url,
              description: item.description,
              ...(item.price !== undefined && {
                offers: {
                  '@type': 'Offer',
                  price: item.price,
                  priceCurrency: 'VND',
                },
              }),
            },
          }),
        })),
      };

    default:
      return {};
  }
}

export default StructuredData;
